/** Accessible interactions for reusable pricing partials. */

function messagesFor(root) {
  const node = root.querySelector('[data-pricing-messages]');
  return {
    loading: node?.dataset.messageLoading || '',
    previewReady: node?.dataset.messagePreviewReady || '',
    previewError: node?.dataset.messagePreviewError || '',
    activationPending: node?.dataset.messageActivationPending || '',
    actionFailed: node?.dataset.messageActionFailed || '',
    planPreview: node?.dataset.messagePlanPreview || '',
    cancellationPreview: node?.dataset.messageCancellationPreview || '',
  };
}

function setPreviewState(dialog, state, preview, msg, canConfirm = false) {
  const previewEl = dialog.querySelector('.pricing-change-preview');
  const loading = dialog.querySelector('[data-pricing-preview-loading]');
  const amount = dialog.querySelector('[data-pricing-preview-amount]');
  const fallback = dialog.querySelector('[data-pricing-preview-fallback]');
  const confirm = dialog.querySelector('[data-pricing-confirm]');
  if (!previewEl) return;

  previewEl.dataset.pricingPreviewState = state;
  if (loading) {
    loading.hidden = state !== 'loading';
    loading.textContent = msg.loading;
  }
  if (amount) {
    amount.hidden = state !== 'ready';
    amount.textContent = state === 'ready'
      ? `${preview.dueNowLabel}: ${preview.displayAmount}`
      : '';
  }
  if (fallback) fallback.hidden = state !== 'error';
  // Only an injected preview plus confirmed-action adapter enables financial confirmation.
  if (confirm) confirm.disabled = state !== 'ready'
    || !preview?.displayAmount
    || !canConfirm;
}

function trapDialogFocus(dialog, event) {
  if (event.key !== 'Tab') return;
  const focusable = [...dialog.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => element.getClientRects().length > 0);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function init(rootEl = document) {
  const root = rootEl.querySelector('[data-pricing-component-root]');
  if (!root || root.dataset.pricingInitialized === 'true') return;
  root.dataset.pricingInitialized = 'true';

  const messages = messagesFor(root);
  const changeDialog = root.querySelector('[data-pricing-change-dialog]');
  const announcer = root.querySelector('[data-pricing-announcer]');
  const adapter = root.pricingPreviewAdapter;
  const getConfirmAction = () => root.pricingConfirmAction;
  let activeTrigger = null;
  let selectedPlan = null;
  let previewRequestId = 0;
  let pendingConfirmation = false;

  const closeDialog = (dialog) => {
    if (dialog?.open) dialog.close();
    else {
      activeTrigger?.focus();
      activeTrigger = null;
    }
  };

  const loadPreview = async (planKey) => {
    const requestId = ++previewRequestId;
    if (!changeDialog || typeof adapter?.preview !== 'function') {
      if (changeDialog) {
        setPreviewState(
          changeDialog,
          'error',
          null,
          messages,
          false,
        );
      }
      if (announcer) announcer.textContent = messages.previewError;
      return;
    }
    setPreviewState(changeDialog, 'loading', null, messages, false);
    try {
      const preview = await adapter.preview(planKey);
      if (!preview || typeof preview.displayAmount !== 'string' || !preview.displayAmount) {
        throw new TypeError('Price preview did not return a display amount');
      }
      if (
        requestId !== previewRequestId
        || selectedPlan !== planKey
        || !changeDialog.open
      ) return;
      setPreviewState(
        changeDialog,
        'ready',
        preview,
        messages,
        typeof getConfirmAction() === 'function' && !pendingConfirmation,
      );
      if (announcer) announcer.textContent = messages.previewReady;
    } catch (_error) {
      if (
        requestId !== previewRequestId
        || selectedPlan !== planKey
        || !changeDialog.open
      ) return;
      setPreviewState(changeDialog, 'error', null, messages, false);
      if (announcer) announcer.textContent = messages.previewError;
    }
  };

  root.querySelectorAll('[data-pricing-select]').forEach((button) => {
    button.addEventListener('click', async () => {
      if (!changeDialog) return;
      if (pendingConfirmation) return;
      activeTrigger = button;
      selectedPlan = button.dataset.pricingSelect;
      const summary = changeDialog.querySelector('[data-pricing-change-summary]');
      const planKey = changeDialog.querySelector('[data-pricing-plan-key]');
      if (summary) {
        summary.textContent = selectedPlan === 'free'
          ? messages.cancellationPreview
          : messages.planPreview;
      }
      if (planKey) planKey.value = selectedPlan;
      changeDialog.showModal();
      changeDialog.querySelector('[data-pricing-dialog-close]')?.focus();
      await loadPreview(selectedPlan);
    });
  });

  root.querySelectorAll('[data-pricing-open-dialog]').forEach((button) => {
    button.addEventListener('click', () => {
      const dialog = root.querySelector(`#${CSS.escape(button.dataset.pricingOpenDialog)}`);
      if (!dialog) return;
      activeTrigger = button;
      dialog.showModal();
      dialog.querySelector('[data-pricing-dialog-close]')?.focus();
    });
  });

  root.querySelectorAll('[data-pricing-dialog-close]').forEach((button) => {
    button.addEventListener('click', () => closeDialog(button.closest('dialog')));
  });

  root.querySelectorAll('[data-pricing-action="link"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (link.dataset.confirmLink !== 'true') return;
      event.preventDefault();
      const confirmation = root.querySelector('[data-pricing-link-confirmation]');
      if (!confirmation) return;
      activeTrigger = link;
      confirmation.showModal();
      confirmation.querySelector('[data-pricing-dialog-close]')?.focus();
      const confirmLink = confirmation.querySelector('[data-pricing-link-confirm]');
      if (confirmLink) confirmLink.href = link.href;
    });
  });

  root.querySelectorAll('dialog').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener('keydown', (event) => trapDialogFocus(dialog, event));
    dialog.addEventListener('close', () => {
      previewRequestId += 1;
      activeTrigger?.focus();
      activeTrigger = null;
    });
  });

  root.querySelector('[data-pricing-retry]')?.addEventListener('click', async () => {
    if (selectedPlan) await loadPreview(selectedPlan);
  });

  root.querySelectorAll('[data-pricing-retry-action]').forEach((button) => {
    button.addEventListener('click', async () => {
      const retryAction = root.pricingRetryAction;
      if (typeof retryAction !== 'function') return;
      button.disabled = true;
      try {
        await retryAction();
        if (announcer) announcer.textContent = messages.previewReady;
      } catch (_error) {
        if (announcer) announcer.textContent = messages.actionFailed;
      } finally {
        button.disabled = false;
      }
    });
  });

  root.querySelector('[data-pricing-confirmation]')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const confirm = root.querySelector('[data-pricing-confirm]');
    const activation = root.querySelector('[data-pricing-activation]');
    const confirmAction = getConfirmAction();
    if (!selectedPlan || typeof confirmAction !== 'function' || confirm?.disabled) {
      if (announcer) announcer.textContent = messages.previewError;
      return;
    }
    if (pendingConfirmation) return;
    pendingConfirmation = true;
    if (confirm) confirm.disabled = true;
    try {
      // The host owns the real request and its lifecycle. Only success shows pending.
      await confirmAction({ planKey: selectedPlan });
      if (changeDialog.open && activation) {
        activation.hidden = false;
        const activationMessage = activation.querySelector('[data-pricing-activation-message]');
        if (activationMessage) activationMessage.textContent = messages.activationPending;
      }
      if (changeDialog.open && announcer) announcer.textContent = messages.activationPending;
      closeDialog(changeDialog);
    } catch (_error) {
      pendingConfirmation = false;
      if (changeDialog.open) await loadPreview(selectedPlan);
      if (announcer) announcer.textContent = messages.actionFailed;
    }
  });
}

export { setPreviewState };
